import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const releaseNotes = [
  {
    version: '1.0.6',
    releaseDate: '10-Dec-2025',
    sections: [
      {
        title: '1. New Features',
        content: [
          'Project Dashboard: Admins can now access a dedicated project dashboard to monitor ongoing project execution and management.',
          'Task Proirity: Users can now assign priority to tasks both during task creation and directly from the My Task section.',
          'Task attachments: Users can add attachments to the task.'
        ],
      },
      {
        title: '2. How to Update the Application',
        content: [
          'Updating is simple — just perform a hard refresh to ensure you are using the latest version.',
          'For Web Application',
          'For Windows: Press Ctrl + F5.',
          'For Mac: Press Cmd + Shift + R.',
        ],
      },
    ],
  },
  {
    version: '1.0.5',
    releaseDate: '22-Nov-2025',
    sections: [
      {
        title: '1. New Features',
        content: [
          'Task Creation: Tasks can now be created directly from the board for faster workflow.',
          'User Profile: Users can now create and personalize their own profile.',
          'Project Documents Management: Add and manage project wise documents via uploading files or add external links to each project.',
          'Asset Management: Easily organize and manage all company assets.',
          'Stakeholder Feedback/Issue Management: Stakeholder can now submit their feedback or raise issues in the form of tasks.',
        ],
      },
      {
        title: '2. Improvements',
        content: [
          'Action Refinement: Task creation restricted as the allowed manual task.',
          'UI Enhancements: Multiple improvements made to visuals and overall usability.',
          'Task Entry: Improved performance and smoother experience when opening task creation and updating task entry.',
        ],
      },
      {
        title: '3. Bug Fixes',
        content: [
          'Board View: The "Add Task" button now correctly hides when Manual Task Creation is disabled.',
          'List View: Admins can now edit any task by clicking directly on the row.',
          'UI Adjustments: Fixed inconsistencies in My Tasks and Board View for a smoother experience.',
        ],
      },
      {
        title: '4. How to Update the Application',
        content: [
          'Updating is simple — just perform a hard refresh to ensure you are using the latest version.',
          'For Web Application',
          'For Windows: Press Ctrl + F5.',
          'For Mac: Press Cmd + Shift + R.',
        ],
      },
    ],
  },
  {
    version: '1.0.4',
    releaseDate: '9-Sep-2024',
    sections: [
      {
        title: '1. New Features',
        content: [
          "Admin Module: Enabled admin users to review all members' timelines.",
          'Timetracker: Enhanced task visibility by adding progress bars for daily tasks and individual tasks, showing how much time remains for each.',
          'Timetracker: When turning off the timer, users are prompted to update the task status (In Progress, Completed, or Blocked), encouraging them to make a selection for better scoring.',
          'User Table Update: Added a "Mobile Access" column, allowing users to access the timer from mobile devices.',
        ],
      },
      {
        title: '2. Improvements',
        content: [
          'Performance Scoring System: Enhanced the UI, making it more intuitive and user-friendly.',
          'My Task Module: Streamlined the widget by adding a top filter to view tasks by status, eliminating the need to enable and apply filters separately.',
          'Performance Improvement: Optimized queries in the scoring system for improved performance.',
          'UI Adjustment: Refined the UI in various areas for better readability.',
          'Report Tab Layout: Updated the input fields to a "one by two" layout for a cleaner design.',
          'Projects Module: Introduced separate tabs for easy filtering of each field.',
        ],
      },
      {
        title: '3. Bug Fixes',
        content: [
          'Breadcrumbs: Fixed alignment of all breadcrumbs.',
          'Bug Fix: The page was getting distorted in the Projects tab.',
          'UI Issue: New project boxes alignment needed adjustment.',
          'Text Changes: Made adjustments to the text throughout the application to improve readability and overall appearance.',
          'Minor Fixes: Additional minor fixes for better user experience.',
        ],
      },
      {
        title: '4. How to Update the Application',
        content: [
          'Updating the application is simple and can be done using a hard refresh. Follow these steps to ensure you have the latest version:',
          'For Web Applications:',
          'Open the application in your web browser.',
          'For Windows: Press Ctrl + F5',
          'For Mac: Cmd + Shift + R.',
        ],
      },
    ],
  },
  {
    version: '1.0.3',
    releaseDate: '28-Aug-2024',
    sections: [
      {
        title: '1. New Features',
        content: [
          'Implemented Performance Scoring System:',
          'Task Completion Points: You will earn points based on the planned hours for each task. For example, if a task is planned for 5 hours, you will earn 5 points upon completing it within those 5 hours. However, if you take longer to complete the task, your points will decrease proportionally. For instance, completing the task in 6 hours will earn you 4 points. Conversely, you can earn bonus points for completing tasks early, such as 6 points for finishing a 5-hour task in 4 hours.',
          'Issue-Related Point Deductions: Points will be deducted based on the severity of issues arising from your work. Critical Issues: Deduct 100% of the points for that task. Major Issues: Deduct 50% of the points for that task. Minor Issues: Deduct 10% of the points for that task.',
          'Attendance Points: You will receive 40 attendance points each month. These points will be deducted under the following circumstances: Underworking: If you work 6 hours or less in a day, 2 points will be deducted. Overworking: If you log more than 10 hours in a day, 1 point will be deducted.',
          'Search Enhancement: Added the ability for Admin users to search for employees in the Dashboard.',
          "Project Member Allocation: Implemented a mechanism to define team members' planned start and end dates for allocated projects.",
          'Team Member Status: Added functionality to mark employees as active or inactive for a certain duration based on their start and end dates.',
          'Time Tracking Control: Admin can now enable or disable the time tracking feature for users, allowing them to track time from their mobile devices.',
          'Project Completion Date: The project completion date is now set as the end date for all project team members.',
          'Time Tracker Restriction: Members are restricted from running the time tracker once the project is completed.',
          'User Table Update: Added a score column to the user table.',
          'Task Report Search: Introduced a search feature in reports based on task type.',
          'Task Type in Reports: Added task type information to the report data.',
          'Project End Reminder: Reminder emails will be sent to the project manager and owner 15 days before the project ends.',
        ],
      },
      {
        title: '2. Improvements',
        content: [
          "Task Interface Update: Removed checkboxes from the 'My Task' section for a cleaner UI.",
          'API Enhancement: Added a filter option in the "Get All My Task" API to remove pagination if needed.',
          'Performance Improvement: Optimized query in the scoring system for better performance.',
          'Report Enhancement: Added search functionality for task type in reports and included the task type in the report data.',
          'Task Creation Fix: Removed the default setting of excluding completed tasks when creating a new task.',
          'UI Adjustment: Decreased the text size for filters for better readability.',
          'Task Timer Control: Added play/pause icons in the "My Task" table to control the task timer (backend).',
          'Email Reminder Automation: Implemented a cron job for sending project reminder emails.',
          'Report Tab Layout: Adjusted the input field in the Report tab to a "one by two" layout for improved design.',
        ],
      },
      {
        title: '3. Bug Fixes',
        content: [
          'Breadcrumbs: Fixed alignment of all breadcrumbs.',
          'Bug Fix: Error occurred when trying to create a task from the task board.',
          'UI Issue: New project boxes alignment needed adjustment.',
          'Visibility Issue: Users are now able to see other users’ tasks in the project’s task list.',
          'Text Changes: Made adjustments to the text throughout the application to improve readability and overall appearance.',
          'Username Issue: Fixed Username Issue in Topbar.',
          'Minor Fixes: Additional minor fixes for better user experience.',
        ],
      },
      {
        title: '4. How to Update the Application',
        content: [
          'Updating the application is simple and can be done using a hard refresh. Follow these steps to ensure you have the latest version:',
          'For Web Applications:',
          'Open the application in your web browser.',
          'For Windows: Press Ctrl + F5',
          'For Mac: Cmd + Shift + R.',
        ],
      },
    ],
  },
  {
    version: '1.0.2',
    releaseDate: '17-July-2024',
    sections: [
      {
        title: '1. New Features',
        content: [
          'My Tasks: Add a checkbox column for multiple select actions.',
          'Task Level URL: Add a URL to open detailed task information.',
          "Project Member Allocation: Implement a mechanism to define team members' planned start and end dates for allocated projects.",
          "Status Column: Add a new column for 'On Hold' status. Trigger a holding date when 'On Hold' is selected.",
          'Holiday List and Quarterly Goals: Add a new tab in the project section for organizational holiday lists and quarterly goals.',
          'Upload File Icon: Add an eye icon on projects to indicate how to upload a file.',
          'Settings Sections: In the settings section of the application, create two subsections: Change Password, Email Subscription.',
          "Project Status Sections: At the top of any project, have four sections that define its status: 'Active,' 'Archive,' 'Complete,' and 'On Hold.'",
        ],
      },
      {
        title: '2. Improvements',
        content: [
          'Default View: Set the board view as the default view.',
          'Bulk Upload Issues: Enable issues to be added via bulk upload.',
          'Export Functionalities: Enhance export functionalities for task creation and updates by adding more columns.',
          'Template Instructions: Provide a downloadable template for data entry.',
          'File Upload Feedback: Display a toast message for unsuccessful file uploads, including the reason for failure.',
          'Template Examples: Add examples in the templates.',
          'Timeline: Include planned start and end dates in the timeline status.',
          'Completed Tasks: Remove completed tasks from projects.',
        ],
      },
      {
        title: '3. Bug Fixes',
        content: [
          'Correction in Board view, mixing of tasks from other projects.',
          'Text Changes: Made adjustments to the text throughout the application to improve readability and overall appearance.',
          'Project Tab Alignment: Proper alignment in the Projects tab is now ensured.',
          'Minor Fixes: Additional minor fixes for better user experience.',
        ],
      },
      {
        title: '4. How to Update the Application',
        content: [
          'Updating the application is simple and can be done using a hard refresh. Follow these steps to ensure you have the latest version:',
          'For Web Applications:',
          'Open the application in your web browser.',
          'For Windows: Press Ctrl + F5',
          'For Mac: Cmd + Shift + R.',
        ],
      },
    ],
  },
  {
    version: '1.0.1',
    releaseDate: '03-July-2024',
    sections: [
      {
        title: '1. New Features',
        content: [
          'Filters and Sorting: Added in all lists across the application, including Users, Project List, and My Task.',
          'Effort Restriction: Team members are now restricted from logging efforts after their respective tasks are completed.',
          'Team Member Activity: Team members can now be marked as active or inactive for allocated projects.',
          'Overall Project Efforts Utilization Widget: Similar to the Team Members Efforts Utilization (Planned vs Actual), this widget is now available on the Project Page.',
          'Import Functionality for Task Creation: Bulk upload functionality is now available for managers.',
          'Reminders and Alerts: Overdue tasks and over-budgeted tasks now trigger email reminders and alerts. Additionally, clicking on the task or project will take you directly to the relevant section in Clockies.',
          'Task Status Colors: Different colors for different task statuses for better visual differentiation.',
        ],
      },
      {
        title: '2. Improvements',
        content: [
          "Task Status Update: Changed 'TO DO' to 'Not Started' for clearer understanding.",
          'Active Projects Options: Added a three-item list with options: Mark as Completed, Mark as Archived, Mark as Active.',
          'Mandatory End Date: The end date is now mandatory when creating any task.',
          "Task Filtering: By default, only 'In Progress' and 'Not Started' tasks are shown. Completed tasks can be viewed by applying a filter in the Projects Information list.",
          'Delay Calculation: Days delayed are no longer calculated for completed tasks.',
          'Tab Highlighting: Active tabs are highlighted with different colors and breadcrumbs are added for easier navigation.',
          'Parent ID Removal: Parent IDs are now removed from the display.',
          'Uniform Table Format: All tables across the application are now in the same format for consistency.',
          'Project Tab Enhancements: Consumed hours and remaining hours are now displayed clearly in the Projects tab.',
          "Today's Tasks: For all today's tasks, efforts, and completion status are now displayed.",
        ],
      },
      {
        title: '3. Bug Fixes',
        content: [
          'Font Alignment: Fonts are now consistently aligned for enhanced readability.',
          'Capitalization: The first letter of every word is capitalized throughout the application.',
          'Project Tab Alignment: Proper alignment in the Projects tab is now ensured.',
          'Minor Fixes: Additional minor fixes for better user experience.',
        ],
      },
      {
        title: '4. How to Update the Application',
        content: [
          'Updating the application is simple and can be done using a hard refresh. Follow these steps to ensure you have the latest version:',
          'For Web Applications:',
          'Open the application in your web browser.',
          'For Windows: Press Ctrl + F5',
          'For Mac: Cmd + Shift + R.',
        ],
      },
    ],
  },
];

const ReleaseNotes = () => (
  <Container>
    <Typography variant="h4" gutterBottom>
      Clockies Release Document
    </Typography>
    {releaseNotes.map((release, index) => (
      <Accordion key={index}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Clockies - Version {release.version}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ paddingLeft: '16px' }}>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Release Date:</strong> {release.releaseDate}
          </Typography>
          {release.sections.map((section, idx) => (
            <div key={idx} style={{ paddingLeft: '16px', marginBottom: '5px' }}>
              <Typography variant="h6" gutterBottom>
                {section.title}
              </Typography>
              <List>
                {section.content.map((item, id) => (
                  <ListItem key={id} sx={{ display: 'list-item', listStyleType: 'disc', paddingLeft: '24px' }}>
                    <ListItemText
                      primary={
                        <span>
                          <strong>{item.split(':')[0]}:</strong> {item.split(':')[1]}
                        </span>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </div>
          ))}
        </AccordionDetails>
      </Accordion>
    ))}
  </Container>
);

export default ReleaseNotes;
